
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

import { resetState } from "../app/login/login.slice";
import {
    connectSocket,
    disconnectSocket,
} from "../services/socket";
import { setupAuthInterceptor } from "../services/authInterceptor";

const AuthSocketListener = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const logoutStarted = useRef(false);

    // Define forceLogout before using it in useEffect.
    const forceLogout = useCallback(
        (message) => {
            if (logoutStarted.current) return;

            logoutStarted.current = true;

            Cookies.remove("token", { path: "/" });
            localStorage.clear();
            sessionStorage.clear();

            dispatch(resetState());
            disconnectSocket();

            toast.error(
                message || "Your session has ended. Please log in again."
            );

            navigate("/login", { replace: true });
        },
        [dispatch, navigate]
    );

    // API fallback listener.
    useEffect(() => {
        const handleApiForceLogout = (event) => {
            forceLogout(event.detail?.message);
        };

        window.addEventListener(
            "app:force-logout",
            handleApiForceLogout
        );

        const cleanupInterceptor = setupAuthInterceptor();

        return () => {
            window.removeEventListener(
                "app:force-logout",
                handleApiForceLogout
            );

            cleanupInterceptor();
        };
    }, [forceLogout]);

    // Socket.IO listener.
    useEffect(() => {
        const token = Cookies.get("token");

        if (!token) return;

        const socket = connectSocket(token);

        if (!socket) return;

        const handleForceLogout = (payload) => {

            forceLogout(
                payload?.message ||
                "Your account session has ended. Please log in again."
            );
        };

        const handleConnectError = (error) => {
            const code = error?.data?.code || error?.message;

            if (
                [
                    "UNAUTHORIZED",
                    "ACCOUNT_DISABLED",
                    "TOKEN_REVOKED",
                ].includes(code)
            ) {
                forceLogout(
                    "Your session is no longer valid. Please log in again."
                );
            }
        };

        socket.on("auth:force-logout", handleForceLogout);
        socket.on("connect_error", handleConnectError);

        return () => {
            socket.off("auth:force-logout", handleForceLogout);
            socket.off("connect_error", handleConnectError);
        };
    }, [forceLogout]);

    return null;
};

export default AuthSocketListener;
